import React from "react"
import { graphql } from "gatsby"

import { SEO } from "components"
import {
  Accordion,
  CareerCards,
  CareerDropdown,
  Collage,
  ColumnGrid,
  Embed,
  Form,
  Gallery,
  ImageCards,
  LocationList,
  ProductList,
  QuoteList,
  SectionHeader,
  SideBySide,
  StatGrid,
  TalkingShopFeed,
  TeamGrid,
  Testimonial,
  TextBlock,
  Timeline,
  WhyPageCards,
} from "slices"

import Hero from "./_Hero"

function Why({ data, pageContext }) {
  const { contentfulWhyPage } = data

  return (
    <>
      <SEO
        title={contentfulWhyPage?.seoTitle}
        description={contentfulWhyPage?.seoDescription?.internal?.content}
        image={contentfulWhyPage?.seoImage}
      />
      <main>
        <Hero {...contentfulWhyPage} />
        {contentfulWhyPage?.content?.map(({ __typename, ...rest }, i) => {
          switch (__typename) {
            case "ContentfulAccordion": {
              return <Accordion {...rest} key={`content-accordion--${i}`} />
            }
            case "ContentfulCareerCards": {
              return <CareerCards {...rest} key={`content-careerCards--${i}`} />
            }
            case "ContentfulCareerDropdown": {
              return (
                <CareerDropdown
                  {...rest}
                  key={`content-careerDropdown--${i}`}
                />
              )
            }
            case "ContentfulCollage": {
              return <Collage {...rest} key={`content-collage--${i}`} />
            }
            case "ContentfulColumnGrid": {
              return <ColumnGrid {...rest} key={`content-columnGrid--${i}`} />
            }
            case "ContentfulEmbed": {
              return <Embed {...rest} key={`content-embed--${i}`} />
            }
            case "ContentfulForm": {
              return <Form {...rest} key={`content-form--${i}`} />
            }
            case "ContentfulGallery": {
              return <Gallery {...rest} key={`content-gallery--${i}`} />
            }
            case "ContentfulImageCards": {
              return <ImageCards {...rest} key={`content-imageCards--${i}`} />
            }
            case "ContentfulLocationList": {
              return (
                <LocationList {...rest} key={`content-locationList--${i}`} />
              )
            }
            case "ContentfulProductList": {
              return <ProductList {...rest} key={`content-productList--${i}`} />
            }
            case "ContentfulQuoteList": {
              return <QuoteList {...rest} key={`content-quoteList--${i}`} />
            }

            case "ContentfulSectionHeader": {
              return (
                <SectionHeader {...rest} key={`content-sectionHeader--${i}`} />
              )
            }
            case "ContentfulSideBySide": {
              return <SideBySide {...rest} key={`content-sideBySide--${i}`} />
            }
            case "ContentfulStatGrid": {
              return <StatGrid {...rest} key={`content-statGrid--${i}`} />
            }
            case "ContentfulTalkingShopFeed": {
              return (
                <TalkingShopFeed
                  {...rest}
                  feeds={pageContext.talkingShopFeeds}
                  key={`content-talkingShopFeed--${i}`}
                />
              )
            }
            case "ContentfulTeamGrid": {
              return <TeamGrid {...rest} key={`content-teamGrid--${i}`} />
            }
            case "ContentfulTestimonial": {
              return <Testimonial {...rest} key={`content-testimonial--${i}`} />
            }
            case "ContentfulTextBlock": {
              return <TextBlock {...rest} key={`content-textBlock--${i}`} />
            }
            case "ContentfulTimeline": {
              return <Timeline {...rest} key={`content-timeline--${i}`} />
            }
            case "ContentfulWhyPageCards": {
              return (
                <WhyPageCards {...rest} key={`content-whyPageCards--${i}`} />
              )
            }
            default:
              return null
          }
        })}
      </main>
    </>
  )
}
export const pageQuery = graphql`
  query WhyQuery($slug: String) {
    contentfulWhyPage(slug: { eq: $slug }) {
      seoTitle
      seoDescription {
        internal {
          content
        }
      }
      seoImage {
        ...AssetFragment
      }
      slug
      heroHeading
      heroContent {
        raw
      }
      heroImage {
        ...AssetFragment
        gatsbyImageData(width: 600, layout: FULL_WIDTH)
      }
      heroVideo {
        ...AssetFragment
      }
      heroDecorators {
        ...DecoratorFragment
      }
      content {
        __typename
        ... on ContentfulGallery {
          galleryVideo
          images {
            ...AssetFragment
            small: gatsbyImageData(width: 358, placeholder: BLURRED)
            large: gatsbyImageData(width: 810, placeholder: BLURRED)
          }
        }
        ... on ContentfulLocationList {
          heading
          subheading {
            internal {
              content
            }
          }
          image {
            description
            small: gatsbyImageData(
              width: 179
              aspectRatio: 1
              resizingBehavior: PAD
            )
            large: gatsbyImageData(
              width: 389
              aspectRatio: 1
              resizingBehavior: PAD
            )
            file {
              contentType
              url
            }
          }
          locationTypes {
            name
          }
        }
        ... on ContentfulWhyPageCards {
          title
          size
          links {
            slug
            heroHeading
            heroImage {
              ...AssetFragment
              gatsbyImageData(width: 600)
            }
            heroVideo {
              ...AssetFragment
            }
          }
        }
        ... on ContentfulSectionHeader {
          header: heading {
            heading
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulSideBySide {
          heading
          image {
            ...AssetFragment
            gatsbyImageData(width: 600, placeholder: BLURRED, quality: 80)
          }
          decorators {
            ...DecoratorFragment
          }
          content {
            raw
          }
        }
        ... on ContentfulStatGrid {
          heading
          subheading {
            raw
          }
          focusText
          focusImage {
            ...AssetFragment
            gatsbyImageData(width: 768)
          }
          focusVideo {
            ...AssetFragment
          }
          statItems {
            title
            content {
              raw
            }
          }
        }
        ... on ContentfulTextBlock {
          content {
            raw
          }
          button {
            ...LinkFragment
          }
          decorators {
            ...DecoratorFragment
          }
        }
        ... on ContentfulColumnGrid {
          align
          heading
          subheading {
            raw
          }
          linkCard {
            slug
            category {
              name
              slug
            }
            title
            image {
              ...AssetFragment
              gatsbyImageData(width: 720, placeholder: BLURRED)
            }
          }
          columnGridCards {
            color
            image {
              ...AssetFragment
              gatsbyImageData(width: 512)
            }
            text {
              raw
            }
          }
        }
        ... on ContentfulTalkingShopFeed {
          content {
            raw
          }
          type {
            slug
            name
          }
        }
        ... on ContentfulCollage {
          collageText
          content {
            raw
          }
          images {
            ...AssetFragment
            gatsbyImageData(width: 227, aspectRatio: 1, placeholder: BLURRED)
          }
        }
      }
    }
  }
`

export default Why
